import React, { useState, useEffect } from "react";
import EducationForm from "./EducationForm";
import EducationCard from "./EducationCard";
import { sectionsAPI } from "../../../services/sectionsAPI";
import { formatAPIError } from "../../../utils/validation";
import "./styles/EducationSection.css";

const EducationSection = () => {
  const [educations, setEducations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [formLoading, setFormLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingEducation, setEditingEducation] = useState(null);

  // custom delete confirmation
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [deleteId, setDeleteId] = useState(null);

  // Load educations
  useEffect(() => {
    loadEducations();
  }, []);

  const loadEducations = async () => {
    try {
      setLoading(true);
      setError("");
      const response = await sectionsAPI.education.getAll();
      setEducations(response.data || []);
    } catch (err) {
      setError(formatAPIError(err));
    } finally {
      setLoading(false);
    }
  };

  const handleAddEducation = () => {
    setEditingEducation(null);
    setIsFormOpen(true);
  };

  const handleEditEducation = (education) => {
    setEditingEducation(education);
    setIsFormOpen(true);
  };

  const handleFormSubmit = async (formData) => {
    try {
      setFormLoading(true);
      setError("");
      setSuccess("");

      if (editingEducation) {
        await sectionsAPI.education.update(editingEducation.id, formData);
        setSuccess("✅ Education updated successfully!");
      } else {
        await sectionsAPI.education.add(formData);
        setSuccess("✅ Education added successfully!");
      }

      await loadEducations();
      setIsFormOpen(false);
      setEditingEducation(null);
      setTimeout(() => setSuccess(""), 3000);
    } catch (err) {
      setError(formatAPIError(err));
    } finally {
      setFormLoading(false);
    }
  };

  // open custom confirm dialog instead of window.confirm
  const askDeleteEducation = (id) => {
    setDeleteId(id);
    setConfirmOpen(true);
  };

  const closeConfirm = () => {
    setConfirmOpen(false);
    setDeleteId(null);
  };

  const confirmDelete = async () => {
    if (!deleteId) return;

    try {
      setError("");
      setSuccess("");
      await sectionsAPI.education.delete([deleteId]);
      setSuccess("✅ Education deleted successfully!");
      await loadEducations();
      setTimeout(() => setSuccess(""), 3000);
    } catch (err) {
      setError(formatAPIError(err));
    } finally {
      closeConfirm();
    }
  };

  const handleFormClose = () => {
    setIsFormOpen(false);
    setEditingEducation(null);
  };

  if (loading) {
    return (
      <div className="education-section">
        <div className="education-header">
          <h2 className="education-heading">Education</h2>
          <button
            type="button"
            className="education-add-btn"
            onClick={handleAddEducation}
          >
            <span className="education-add-plus">+</span>
            Add Education
          </button>
        </div>

        <div className="loading-container">
          <div className="spinner" />
          <p>Loading educations...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="education-section">
      <div className="education-header">
        <h2 className="education-heading">Education</h2>
        <button
          type="button"
          className="education-add-btn"
          onClick={handleAddEducation}
        >
          <span className="education-add-plus">+</span>
          Add Education
        </button>
      </div>

      {success && (
        <div className="alert alert-success">
          <span>{success}</span>
        </div>
      )}
      {error && (
        <div className="alert alert-error">
          <span>{error}</span>
        </div>
      )}

      
        <div className="education-list">
          {educations.map((education) => (
            <EducationCard
              key={education.id}
              education={education}
              isSelected={false}
              onSelect={() => {}}
              onEdit={() => handleEditEducation(education)}
              onDelete={() => askDeleteEducation(education.id)}
            />
          ))}
        </div>
      

      <EducationForm
        isOpen={isFormOpen}
        education={editingEducation}
        onClose={handleFormClose}
        onSubmit={handleFormSubmit}
        loading={formLoading}
      />

      {/* Custom delete confirmation dialog */}
      {confirmOpen && (
  <div className="confirm-overlay">
    <div className="confirm-dialog">
      <h3 className="confirm-title">Are you sure?</h3>
      <p className="confirm-body">
        This action cannot be undone. This will permanently delete this
        education entry.
      </p>
      <div className="confirm-actions">
        <button className="confirm-btn confirm-cancel" onClick={closeConfirm}>
  Cancel
</button>
<button className="confirm-btn confirm-delete" onClick={confirmDelete}>
  Delete
</button>

      </div>
    </div>
  </div>
)}

    </div>
  );
};

export default EducationSection;
